import {
  useRef,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { ArrowRight, Paperclip } from 'lucide-react';

import type { ChatDto, MessageDto } from '../carrierCabinet/api';
import { ProfileLink } from '../../ui/ProfileLink/ProfileLink';
import { useLanguage } from '../../i18n/LanguageProvider';
import { readStoredLanguage } from '../../i18n/locale';
import { getRuntimeTimezone } from '../../i18n/preferences';
import { ChatAttachment, formatSystemMessage, validateChatAttachment } from './messageUtils';
import './chat.css';

export interface UnifiedChatProps {
  children?: ReactNode;
  className?: string;
  isCompactList?: boolean;
}

export function ChatAppContainer({ children, className = '', isCompactList = false }: UnifiedChatProps) {
  return (
    <section className={`${isCompactList ? 'customer-chat-list__app' : 'customer-chat__app'} ${className}`}>
      {children}
    </section>
  );
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export function relativeTime(value: string): string {
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} дн назад`;
  return new Date(value).toLocaleDateString(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', { timeZone: getRuntimeTimezone() });
}

export function formatDateTime(value: string): string {
  const date = new Date(value);
  const locale = readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU';
  const timeZone = getRuntimeTimezone();
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone }).format(date);
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', timeZone }).format(date);
  return `${day} в ${time}`;
}

export interface UnifiedChatSidebarProps {
  chats: ChatDto[];
  partnerNameKey: 'carrier_name' | 'customer_name';
  query: string;
  selectedId?: number | null;
  setQuery: (val: string) => void;
  onSelectChat: (chat: ChatDto) => void;
}

export function UnifiedChatSidebar({
  chats,
  partnerNameKey,
  query,
  selectedId,
  setQuery,
  onSelectChat,
}: UnifiedChatSidebarProps) {
  const { language } = useLanguage();

  const visible = chats.filter((chat) => {
    const partnerName = (chat[partnerNameKey] ?? '') as string;
    const match = [
      partnerName,
      chat.from_city,
      chat.to_city,
      chat.last_message?.body ?? '',
    ].some((field) => field.toLowerCase().includes(query.toLowerCase()));
    return match;
  });

  return (
    <aside className="customer-chat-sidebar">
      <div className="customer-chat-search">
        <input
          aria-label="Поиск по чатам"
          placeholder="Поиск по чатам"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <div className="customer-chat-rows">
        {visible.map((chat) => {
          const partnerName = (chat[partnerNameKey] ?? '') as string;
          const isActive = chat.id === selectedId;
          const lastMsg = chat.last_message;

          let previewText = 'Нет сообщений';
          if (lastMsg) {
            if (['media', 'photo'].includes(lastMsg.message_type)) {
              previewText = lastMsg.media_file?.original_filename ?? 'Вложение';
            } else {
              previewText = formatSystemMessage(lastMsg.body ?? '', language);
            }
          }

          return (
            <button
              key={chat.id}
              className={isActive ? 'is-active' : ''}
              type="button"
              onClick={() => onSelectChat(chat)}
            >
              <i aria-hidden="true" />
              <span className="customer-chat-avatar">{initials(partnerName)}</span>
              <span className="customer-chat-row__content">
                <span>
                  <strong>{partnerName}</strong>
                  {lastMsg ? (
                    <time dateTime={lastMsg.created_at}>
                      {relativeTime(lastMsg.created_at)}
                    </time>
                  ) : null}
                </span>
                <small>
                  {chat.from_city} → {chat.to_city}
                </small>
                <em data-i18n-skip>{previewText}</em>
              </span>
              {chat.unread_count > 0 ? (
                <b className="customer-chat-row__unread">
                  {chat.unread_count > 99 ? '99+' : chat.unread_count}
                </b>
              ) : null}
            </button>
          );
        })}
      </div>
    </aside>
  );
}

export interface UnifiedChatHeaderProps {
  partnerName: string;
  partnerRole?: 'carrier' | 'customer';
  partnerProfileId?: number | null;
  rating?: string | number | null;
  requestCode: string;
  fromCity: string;
  toCity: string;
  statusLabel?: string | null;
  socketState: 'connecting' | 'open' | 'closing' | 'closed';
  actions?: ReactNode;
}

export function UnifiedChatHeader({
  partnerName,
  partnerRole,
  partnerProfileId,
  rating,
  requestCode,
  fromCity,
  toCity,
  statusLabel,
  socketState,
  actions,
}: UnifiedChatHeaderProps) {
  return (
    <header className="customer-chat__header">
      <div className="customer-chat__head-info">
        <strong>
          {partnerRole ? (
            <ProfileLink profileId={partnerProfileId} role={partnerRole}>{partnerName}</ProfileLink>
          ) : partnerName}
          {rating ? <small>★ {rating}</small> : null}
        </strong>
        <div className="customer-chat__head-meta">
          <div className="customer-chat__head-meta-row">
            <span>Заявка #{requestCode}</span>
            <span className="customer-chat__head-dot">•</span>
            <span>
              {fromCity} → {toCity}
            </span>
          </div>
          <div className="customer-chat__head-meta-row">
            {statusLabel ? (
              <>
                <span>{statusLabel}</span>
                <span className="customer-chat__head-dot">•</span>
              </>
            ) : null}
            <small className={`customer-chat__connection is-${socketState}`}>
              {socketState === 'open' ? 'Онлайн' : 'Переподключение…'}
            </small>
          </div>
        </div>
      </div>
      {actions ? <div className="customer-chat__head-actions">{actions}</div> : null}
    </header>
  );
}

export interface UnifiedMessageBubbleProps {
  currentUserId: number;
  message: MessageDto;
}

export function UnifiedMessageBubble({ currentUserId, message }: UnifiedMessageBubbleProps) {
  const { language } = useLanguage();

  if (['system', 'payment', 'status'].includes(message.message_type)) {
    return (
      <div className={`customer-chat-message is-${message.message_type}`} data-i18n-skip>
        {formatSystemMessage(message.body, language)}
      </div>
    );
  }

  const isOwn = message.sender === currentUserId;

  return (
    <article className={`customer-chat-message is-bubble${isOwn ? ' is-own' : ''}`}>
      {message.media_file?.url ? (
        <ChatAttachment file={message.media_file} alt={message.body || 'Вложение в чате'} />
      ) : null}
      {message.body ? <p data-i18n-skip>{message.body}</p> : null}
      <footer>
        <time dateTime={message.created_at}>{formatDateTime(message.created_at)}</time>
        {isOwn ? (
          <span>{message.is_read_by_recipient ? 'Прочитано' : 'Отправлено'}</span>
        ) : null}
      </footer>
    </article>
  );
}

export interface UnifiedMessageListProps {
  currentUserId: number;
  loadingOlder?: boolean;
  messages: MessageDto[];
  newMessagesCount?: number;
  nextBefore?: number | null;
  viewportRef: RefObject<HTMLDivElement | null>;
  onLoadOlder?: () => void;
  onScrollToBottom?: () => void;
}

export function UnifiedMessageList({
  currentUserId,
  loadingOlder = false,
  messages,
  newMessagesCount = 0,
  nextBefore,
  viewportRef,
  onLoadOlder,
  onScrollToBottom,
}: UnifiedMessageListProps) {
  return (
    <div ref={viewportRef} className="customer-chat__messages">
      {nextBefore ? (
        <button
          className="chat-load-older"
          disabled={loadingOlder}
          type="button"
          onClick={onLoadOlder}
        >
          {loadingOlder ? 'Загрузка…' : 'Предыдущие сообщения'}
        </button>
      ) : null}

      {messages.map((message) => (
        <UnifiedMessageBubble
          key={message.id}
          currentUserId={currentUserId}
          message={message}
        />
      ))}

      {newMessagesCount > 0 && onScrollToBottom ? (
        <button
          className="chat-new-messages"
          type="button"
          onClick={onScrollToBottom}
        >
          {newMessagesCount} новых сообщений
        </button>
      ) : null}
    </div>
  );
}

export interface UnifiedChatComposerProps {
  attachmentError?: string;
  disabled?: boolean;
  draft: string;
  sending?: boolean;
  uploadProgress?: number | null;
  onSend: (event: FormEvent) => void;
  onSetDraft: (text: string) => void;
  onUploadFile: (file: File) => void;
}

export function UnifiedChatComposer({
  attachmentError,
  disabled = false,
  draft,
  sending = false,
  uploadProgress,
  onSend,
  onSetDraft,
  onUploadFile,
}: UnifiedChatComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const error = validateChatAttachment(file);
    if (error) {
      alert(error);
      return;
    }
    onUploadFile(file);
  }

  return (
    <form className="customer-chat__composer" onSubmit={onSend}>
      <input
        ref={fileInputRef}
        hidden
        type="file"
        accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,application/pdf,.docx,.xlsx"
        onChange={handleFileChange}
      />

      <button
        aria-label="Прикрепить файл"
        disabled={disabled || sending}
        title="Прикрепить файл"
        type="button"
        onClick={() => fileInputRef.current?.click()}
      >
        <Paperclip aria-hidden="true" size={18} strokeWidth={1.5} />
      </button>

      <input
        aria-label="Сообщение"
        disabled={disabled || sending}
        placeholder="Напишите сообщение…"
        type="text"
        value={draft}
        onChange={(event) => onSetDraft(event.target.value)}
      />

      <button
        aria-label="Отправить"
        className="customer-chat__send"
        disabled={disabled || sending || !draft.trim()}
        type="submit"
      >
        <span>Отправить</span>
        <ArrowRight aria-hidden="true" size={18} />
      </button>

      {uploadProgress !== null && uploadProgress !== undefined ? (
        <div className="chat-upload-progress">
          <i style={{ width: `${uploadProgress}%` }} />
          <span>Загрузка {uploadProgress}%</span>
        </div>
      ) : null}

      {attachmentError ? (
        <div className="chat-attachment-error">
          <span>{attachmentError}</span>
        </div>
      ) : null}
    </form>
  );
}
